import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  Leaf,
  Lock,
  Mail,
  Phone,
  Shield,
  Sparkles,
  User,
  UserPlus,
} from 'lucide-react';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';
import { useTenant } from '../../contexts/TenantContext';
import {
  PoweredByUseAmbiental,
  TenantBanner,
  TenantCoatMark,
} from '../../components/Brand/TenantBrandMark';
import { tenantBrand } from '../../utils/tenantBranding';
import { identityService } from '../../services/api';
import { formatCpf } from '../../utils/cpf';
import { userFacingError } from '../../i18n/userFacingError';

export default function RegisterRequerente() {
  const { t } = useTranslation(['auth', 'common']);
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: '',
    cpf: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  const brand = tenantBrand(tenant);
  const orgName = tenant?.name || t('brandTitle');
  const highlights = [
    { icon: UserPlus, title: t('requerenteSignupTitle'), text: t('requerenteSignupLead') },
    { icon: Shield, title: t('highlightTenant'), text: t('highlightTenantText') },
  ];

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'cpf' ? formatCpf(value) || value.replace(/\D/g, '').slice(0, 11) : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    const cpf = formatCpf(form.cpf);
    if (!form.name.trim() || form.name.trim().length < 2) {
      nextErrors.name = t('common:requiredField');
    }
    if (!cpf) {
      nextErrors.cpf = t('cpfRequired');
    }
    if (!form.email.trim()) {
      nextErrors.email = t('common:requiredField');
    }
    if (form.password.length < 8) {
      nextErrors.password = t('setupPlaceholder');
    }
    if (form.password !== form.confirmPassword) {
      nextErrors.confirmPassword = t('passwordsMismatch');
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast.error(t('common:formFixErrors'));
      return;
    }
    setLoading(true);
    try {
      await identityService.signupRequerente({
        name: form.name.trim(),
        cpf,
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        password: form.password,
      });
      toast.success(t('requerenteSignupSuccess'));
      navigate('/login', { replace: true, state: { username: form.email.trim() } });
    } catch (err) {
      toast.error(userFacingError(err, 'registerError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(46,125,50,0.35),transparent)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-primary-700/25 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-emerald-600/20 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(rgba(255,255,255,0.15)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.15)_1px,transparent_1px)] [background-size:48px_48px]"
        aria-hidden
      />

      <div className="relative z-10 flex min-h-screen flex-col lg:flex-row">
        <aside className="relative hidden overflow-hidden border-r border-white/10 bg-gradient-to-b from-primary-950/90 to-slate-950/95 lg:flex lg:w-[44%] lg:flex-col xl:w-[42%]">
          <div className="flex flex-1 flex-col justify-between px-10 py-10 xl:px-14">
            <div>
              <div className="mb-8 flex items-center gap-5">
                {brand.coatOfArmsUrl || brand.logoUrl ? (
                  <TenantCoatMark tenant={tenant} size={104} fallback={false} />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/20 ring-1 ring-primary-400/30">
                    <Leaf className="h-8 w-8 text-primary-300" strokeWidth={1.75} aria-hidden />
                  </div>
                )}
                <div>
                  <PoweredByUseAmbiental light />
                  <p className="mt-1 text-sm font-medium text-slate-300">{orgName}</p>
                </div>
              </div>
              <p className="max-w-md text-lg leading-relaxed text-slate-300">{t('requerenteSignupLead')}</p>
              <ul className="mt-10 space-y-6">
                {highlights.map(({ icon: Icon, title, text }) => (
                  <li key={title} className="flex gap-4">
                    <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-primary-200 ring-1 ring-white/10">
                      <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                    </span>
                    <div>
                      <p className="font-semibold text-white">{title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-400">{text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-10 text-xs text-slate-500">{t('restrictedAccess')}</p>
          </div>
        </aside>

        <main className="flex flex-1 flex-col justify-center px-4 py-10 sm:px-8 lg:px-12 xl:px-16">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            {brand.coatOfArmsUrl || brand.logoUrl ? (
              <TenantCoatMark tenant={tenant} size={56} fallback={false} />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-600/90 shadow-lg shadow-primary-900/40">
                <Leaf className="h-6 w-6 text-white" strokeWidth={1.75} aria-hidden />
              </div>
            )}
            <div>
              <PoweredByUseAmbiental light />
              <p className="text-sm font-semibold text-white">{orgName}</p>
            </div>
          </div>

          <div className="mx-auto w-full max-w-[440px] animate-slide-up">
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.97] shadow-strong backdrop-blur-sm">
              <div className="h-1 bg-gradient-to-r from-primary-600 via-emerald-600 to-primary-800" aria-hidden />
              {brand.bannerUrl ? (
                <div className="bg-white px-6 pt-6 sm:px-8">
                  <TenantBanner tenant={tenant} className="mx-auto h-14 w-full max-h-16" />
                </div>
              ) : null}

              <div className="px-6 pb-8 pt-7 sm:px-8 sm:pt-8">
                <div className="mb-8 flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700 ring-1 ring-primary-100">
                    <Sparkles className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                  </span>
                  <div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900">{t('requerenteSignupTitle')}</h1>
                    <p className="mt-1 text-sm text-gray-600">{t('requerenteSignupLead')}</p>
                  </div>
                </div>

                <form className="space-y-4" onSubmit={submit} noValidate aria-busy={loading}>
                  <div className="form-group mb-0">
                    <label htmlFor="req-name" className="form-label">
                      Nome completo
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                        <User className="h-4 w-4" aria-hidden />
                      </span>
                      <input
                        id="req-name"
                        className={`form-input pl-10 ${errors.name ? 'border-danger-500' : ''}`}
                        name="name"
                        required
                        autoComplete="name"
                        placeholder="Nome completo"
                        value={form.name}
                        onChange={onChange}
                      />
                    </div>
                    {errors.name ? (
                      <p className="form-error mt-1.5 flex items-center gap-1.5">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        {errors.name}
                      </p>
                    ) : null}
                  </div>

                  <div className="form-group mb-0">
                    <label htmlFor="req-cpf" className="form-label">
                      CPF
                    </label>
                    <input
                      id="req-cpf"
                      className={`form-input ${errors.cpf ? 'border-danger-500' : ''}`}
                      name="cpf"
                      required
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="000.000.000-00"
                      value={form.cpf}
                      onChange={onChange}
                    />
                    {errors.cpf ? (
                      <p className="form-error mt-1.5 flex items-center gap-1.5">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        {errors.cpf}
                      </p>
                    ) : null}
                  </div>

                  <div className="form-group mb-0">
                    <label htmlFor="req-email" className="form-label">
                      {t('yourEmail')}
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                        <Mail className="h-4 w-4" aria-hidden />
                      </span>
                      <input
                        id="req-email"
                        className={`form-input pl-10 ${errors.email ? 'border-danger-500' : ''}`}
                        type="email"
                        name="email"
                        required
                        autoComplete="email"
                        placeholder={t('yourEmail')}
                        value={form.email}
                        onChange={onChange}
                      />
                    </div>
                    {errors.email ? (
                      <p className="form-error mt-1.5 flex items-center gap-1.5">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        {errors.email}
                      </p>
                    ) : null}
                  </div>

                  <div className="form-group mb-0">
                    <label htmlFor="req-phone" className="form-label">
                      Telefone <span className="font-normal text-gray-400">(opcional)</span>
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                        <Phone className="h-4 w-4" aria-hidden />
                      </span>
                      <input
                        id="req-phone"
                        className="form-input pl-10"
                        name="phone"
                        autoComplete="tel"
                        placeholder="(00) 00000-0000"
                        value={form.phone}
                        onChange={onChange}
                      />
                    </div>
                  </div>

                  <div className="form-group mb-0">
                    <label htmlFor="req-password" className="form-label">
                      {t('newPassword')}
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                        <Lock className="h-4 w-4" aria-hidden />
                      </span>
                      <input
                        id="req-password"
                        className={`form-input pl-10 ${errors.password ? 'border-danger-500' : ''}`}
                        type="password"
                        name="password"
                        required
                        minLength={8}
                        autoComplete="new-password"
                        placeholder={t('setupPlaceholder')}
                        value={form.password}
                        onChange={onChange}
                      />
                    </div>
                    {errors.password ? (
                      <p className="form-error mt-1.5 flex items-center gap-1.5">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        {errors.password}
                      </p>
                    ) : null}
                  </div>

                  <div className="form-group mb-0">
                    <label htmlFor="req-confirm" className="form-label">
                      {t('confirmPassword')}
                    </label>
                    <input
                      id="req-confirm"
                      className={`form-input ${errors.confirmPassword ? 'border-danger-500' : ''}`}
                      type="password"
                      name="confirmPassword"
                      required
                      minLength={8}
                      autoComplete="new-password"
                      placeholder={t('confirmPassword')}
                      value={form.confirmPassword}
                      onChange={onChange}
                    />
                    {errors.confirmPassword ? (
                      <p className="form-error mt-1.5 flex items-center gap-1.5">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        {errors.confirmPassword}
                      </p>
                    ) : null}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary btn-lg mt-2 w-full shadow-md shadow-primary-900/10 disabled:opacity-70"
                  >
                    {loading ? t('common:sending') : t('requerenteSignupAction')}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <Link to="/login" className="text-sm font-medium text-primary-700 hover:text-primary-800 hover:underline">
                    {t('backToLogin')}
                  </Link>
                </div>
              </div>
            </div>

            <p className="mt-8 text-center text-xs leading-relaxed text-slate-500">{t('footerSecure')}</p>
            <div className="mt-4 flex justify-center">
              <LanguageSwitcher variant="inline" />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
